import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, LayoutDashboard, LineChart, RefreshCw } from 'lucide-react';
import { useEffectiveAccess } from '../../../agenticsaas/contexts/EffectiveAccessContext';
import {
  DASHBOARD_VIEWS,
  dashboardViewLabel,
  getAllowedDashboardViews,
  getDashboardRoutes,
  getStoredDashboardPreference,
  resolveDashboardView,
} from '../../../utils/dashboardPreferences';

const VIEW_META = [
  { id: DASHBOARD_VIEWS.EXECUTIVE, icon: LineChart, label: 'Executive' },
  { id: DASHBOARD_VIEWS.OPERATIONAL, icon: LayoutDashboard, label: 'Operational' },
  { id: DASHBOARD_VIEWS.FINANCE, icon: DollarSign, label: 'Finance View' },
];

export default function DashboardViewSwitcher({ user, currentView, compact = false, variant = 'light' }) {
  const navigate = useNavigate();
  const { role, applications, permissions, subscription, loading: accessLoading } = useEffectiveAccess();
  const options = useMemo(
    () => ({
      orgRole: role,
      access: { role, applications, permissions, subscription, loading: accessLoading },
    }),
    [role, applications, permissions, subscription, accessLoading]
  );
  const routes = getDashboardRoutes();
  const preference = getStoredDashboardPreference();
  const isAuto = preference === 'auto';
  const roleDefault = resolveDashboardView(user, 'auto', options);
  const allowed = useMemo(() => getAllowedDashboardViews(user, options), [user, options]);
  const isDark = variant === 'dark';

  const btnClass = (active) => {
    if (isDark) {
      return `px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
        active
          ? 'bg-blue-600 text-white shadow-sm'
          : 'bg-white/10 text-white border border-slate-600 hover:bg-white/15'
      }`;
    }
    return `px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
      active
        ? 'bg-blue-600 text-white shadow-sm'
        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
    }`;
  };

  const views = VIEW_META.filter((v) => allowed.includes(v.id)).map((v) => ({
    ...v,
    route:
      v.id === DASHBOARD_VIEWS.FINANCE
        ? routes.finance
        : v.id === DASHBOARD_VIEWS.EXECUTIVE
          ? routes.executive
          : routes.operational,
  }));

  if (views.length <= 1) {
    return null;
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? '' : 'mb-4'}`}>
      {!compact && (
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-1">View</span>
      )}
      {views.map(({ id, route, icon: Icon, label }) => (
        <button
          key={id}
          type="button"
          className={btnClass(currentView === id)}
          onClick={() => navigate(route)}
        >
          <span className="inline-flex items-center gap-1.5">
            <Icon className="w-4 h-4" />
            {label}
          </span>
        </button>
      ))}
      {isAuto && (
        <span className={`text-xs ml-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          Auto: {dashboardViewLabel(roleDefault)}
          <button
            type="button"
            className={`ml-1 hover:underline inline-flex items-center gap-0.5 ${
              isDark ? 'text-blue-300' : 'text-blue-600'
            }`}
            onClick={() => navigate('/settings?tab=dashboard')}
          >
            <RefreshCw className="w-3 h-3" />
            change in Settings
          </button>
        </span>
      )}
    </div>
  );
}
